// react
import { useTranslation } from "react-i18next";
import { useContext, useState, Dispatch, SetStateAction } from "react";
// mui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  DialogContentText,
  Card,
  CardContent,
  Typography,
  useTheme,
  styled,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import axios from "axios";
// src
import Icon from "../Icon";
import { config } from "../../config/config";
import ChevronRight from "../../../images/icons/neutral_edit/Chevron Right.svg";
import { MsgSnackbar } from "../../components/MsgSnackbar";
import { Common } from "../../../common/src";
import { deleteContext } from "../../pages/Home";
import SettingDialog from "./SettingDialog";
import MsgErrListSnackbar from "../MsgErrListSnackbar";
import { LoadingButton } from "@mui/lab";
import { formatInt } from "../Utility/Format";
import UploadFileBaseDialog from "./UploadFileBase";

type Props = {
  projectID: Common.Project[];
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  userInfo: Common.LandlogNecessaryUserInfo;
  isARAvailable: boolean;
};

const MenuButton = ({
  projectID,
  setErrorSnackbar,
  setErrorMsg,
  setOpenWarning,
  setMsgWarning,
  userInfo,
  isARAvailable,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [deleting, setDeleteing] = useState(false);
  // 正常 Snackbar
  const [snackbar, setSnackbar] = useState(false);
  const [msg, setMsg] = useState("");
  // エラー Snackbar
  const [errSnackbar, setErrSnackbar] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  //プロジェクト削除有効化フラグ
  const { setIsDelete, getProjectList } = useContext(deleteContext);
  const [openSetting, setOpenSetting] = useState<boolean>(false);

  const handleSnackOpen = () => {
    setSnackOpen(true);
    setIsDelete(true);
    setAnchorEl(null);
  };

  const handleSnackClose = (e: any) => {
    e.stopPropagation();
    setSnackOpen(false);
    setIsDelete(false);
    projectID = [];
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSnackbarClose = async () => {
    setSnackbar(false);
  };

  const handleClickOpen = () => {
    if (projectID.length === 0) return;
    setOpen(true);
  };

  const handleClickClose = () => {
    setOpen(false);
  };

  const handleErrClose = () => {
    setErrSnackbar(false);
    setErrMsg("");
  };

  const handleDelete = async () => {
    let pjId: string[] = [];

    for (var i = 0; i < projectID.length; i++) {
      pjId.push(projectID[i].id);
    }
    const body = { pjIdBody: JSON.stringify(pjId) };
    let deleteFailed: string = "";
    setDeleteing(true);

    await axios
      .delete(`${config.serverHost}/api/v1/project/listDel`, {
        data: body,
        withCredentials: true,
      })
      .then(() => {
        setSnackbar(true);
        setMsg(t("OK"));
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          setErrSnackbar(true);
          for (const listPj of err.response.data) {
            deleteFailed += `${listPj + t("FAILED_TO_DELETE")}\n`;
          }
          setErrMsg(deleteFailed);
        } else {
          setOpenWarning(true);
          setMsgWarning(t("SERVER_TIME_OUT"));
        }
      })
      .finally(() => {
        setDeleteing(false);
      });
    setOpen(false);
    setSnackOpen(false);
    setIsDelete(false);
    projectID = [];
    getProjectList(new AbortController());
  };

  const handleSettingDialogOpen = () => {
    setOpenSetting(true);
    setAnchorEl(null);
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customModalColor = {
    color: theme.palette.base.modal,
  };

  const customBackgroundStyles = {
    background: theme.palette.base.menu,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customButtonStyles = {
    color: theme.palette.base.contrast,
    background: theme.palette.primary.main,
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  const customLoadingButtonColor = {
    background: theme.palette.btn.error.backgroundColor,
    border: theme.palette.btn.error.border,
    boxShadow: "none",
  };

  const RightIcon = () => {
    return (
      <div className="del-pj17">
        <Icon imagePath={ChevronRight} color={customColor.color} />
      </div>
    );
  };

  return (
    <div className="del-pj21">
      <IconButton className="del-pj19" onClick={handleClick}>
        <MoreVert style={customColor} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        style={{ top: "0px", borderRadius: "0px" }}
        PaperProps={{ className: "del-pj18", style: customBackgroundStyles }}
      >
        <MenuItem onClick={handleSnackOpen}>
          {t("SELECT")}
          <RightIcon />
        </MenuItem>
        <MenuItem onClick={handleSettingDialogOpen}>
          {t("SETTING")}
          <RightIcon />
        </MenuItem>
        <UploadFileBaseDialog
          setErrorSnackbar={setErrorSnackbar}
          setErrorMsg={setErrorMsg}
          setOpenWarning={setOpenWarning}
          setMsgWarning={setMsgWarning}
          isARAvailable={isARAvailable}
          setAnchorEl={setAnchorEl}
        />
      </Menu>
      <SettingDialog
        open={openSetting}
        close={() => {
          setOpenSetting(false);
        }}
        userId={userInfo?.userId}
        isDetail={false}
      />
      <Dialog
        open={open}
        PaperProps={{
          className: "del-pj16",
          style: {
            background: customModalColor.color,
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: customDialogBackgroundColor.color,
            },
          },
        }}
      >
        <DialogTitle style={customColor} className="del-pj20">
          {t("REMOVE")}
          <Button
            style={customColor}
            className="x-btn del-pj15"
            onClick={handleClickClose}
          >
            ×
          </Button>
          <div className="border-comm" />
        </DialogTitle>

        <DialogContent>
          <DialogContentText style={customColor} className="del-pj13">
            {formatInt(t("WANT_TO_DELETE"), projectID.length)}
          </DialogContentText>
        </DialogContent>

        <DialogActions className="del-pj12">
          <CustomCanselButton
            onClick={handleClickClose}
            size="small"
            variant="contained"
            className="del-pj9 del-pj11"
          >
            {t("CANCEL")}
          </CustomCanselButton>
          <LoadingButton
            style={customLoadingButtonColor}
            loading={deleting}
            onClick={handleDelete}
            size="small"
            variant="contained"
            className="loading-white del-pj9 del-pj10"
          >
            {t("REMOVE")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <MsgSnackbar open={snackbar} onClose={handleSnackbarClose} msg={msg} />
      <MsgErrListSnackbar
        open={errSnackbar}
        onClose={handleErrClose}
        msg={errMsg}
      />
      <Snackbar
        className="del-pj6 del-pj7"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackOpen}
      >
        <Card
          sx={{ border: `1px solid ${theme.palette.base.contrast}` }}
          className="del-pj2 del-pj4"
          onClick={handleSnackClose}
        >
          <CardContent className="del-pj">
            <Typography className="del-pj1">{t("CANCEL")}</Typography>
          </CardContent>
        </Card>
      </Snackbar>
      <Snackbar
        className="del-pj6 del-pj8"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackOpen}
      >
        <Card
          style={customButtonStyles}
          className="del-pj2 del-pj3"
          onClick={handleClickOpen}
        >
          <CardContent className="del-pj">
            <Typography style={{ color: "#FFFFFF" }} className="del-pj1">
              {formatInt(t("DELETE_SOME_PROJECTS"), projectID.length)}
            </Typography>
          </CardContent>
        </Card>
      </Snackbar>
    </div>
  );
};
export default MenuButton;
